import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  alpha,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AttachMoney,
  Store,
  CreditCard,
  Money,
  CompareArrows,
  Refresh,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { DashboardSettings } from '../types';
import { usePermission } from '../hooks/usePermissions';

// LocalStorage keys
const REVENUE_DASHBOARD_SETTINGS_KEY = 'revenue_dashboard_settings';

interface RevenueData {
  sales: {
    total: number;
  };
  talabatSales: {
    total: number;
  };
  discounts: {
    total: number;
  };
  paymentSummary: {
    prepaid: {
      count: number;
      sales: number;
      talabatSales: number;
    };
    cash: {
      count: number;
      sales: number;
      talabatSales: number;
    };
  };
}

interface RevenueCardProps {
  title: string;
  amount: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  tooltipText?: string;
  comparisonAmount?: number;
  comparisonLabel?: string;
}

const RevenueCard: React.FC<RevenueCardProps> = ({
  title,
  amount,
  subtitle,
  icon,
  color,
  loading = false,
  tooltipText,
  comparisonAmount,
  comparisonLabel
}) => {
  const theme = useTheme();
  
  const content = (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box
            sx={{
              backgroundColor: alpha(
                color === 'primary' ? theme.palette.primary.main :
                color === 'secondary' ? theme.palette.secondary.main :
                color === 'success' ? theme.palette.success.main :
                color === 'error' ? theme.palette.error.main :
                color === 'warning' ? theme.palette.warning.main :
                color === 'info' ? theme.palette.info.main :
                theme.palette.primary.main,
                0.1
              ),
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 
                color === 'primary' ? 'primary.main' :
                color === 'secondary' ? 'secondary.main' :
                color === 'success' ? 'success.main' :
                color === 'error' ? 'error.main' :
                color === 'warning' ? 'warning.main' :
                color === 'info' ? 'info.main' :
                'primary.main'
            }}
          >
            {icon}
          </Box>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="600">{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="h4" color={`${color}.main`} fontWeight="bold" gutterBottom>
              ${amount.toFixed(2)}
            </Typography>
            
            {comparisonAmount !== undefined && comparisonLabel && (
              <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                <Typography variant="body2" color="text.secondary">{comparisonLabel}</Typography>
                <Typography variant="h6" color="text.primary" fontWeight="600">
                  ${(amount - comparisonAmount).toFixed(2)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return tooltipText ? (
    <Tooltip title={tooltipText} arrow placement="top">
      {content}
    </Tooltip>
  ) : content;
};

const RevenueDashboard: React.FC = () => {
  const theme = useTheme();
  
  // Permission checks
  const canViewOrders = usePermission('orders:read');
  
  // State
  const [revenueData, setRevenueData] = useState<RevenueData>({
    sales: { total: 0 },
    talabatSales: { total: 0 },
    discounts: { total: 0 },
    paymentSummary: {
      prepaid: { count: 0, sales: 0, talabatSales: 0 },
      cash: { count: 0, sales: 0, talabatSales: 0 }
    }
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [isReloading, setIsReloading] = useState<boolean>(false); // Master reload state
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // Dashboard settings (no auto-refresh for manager dashboard)
  const [settings, setSettings] = useState<DashboardSettings>(() => {
    const saved = localStorage.getItem(REVENUE_DASHBOARD_SETTINGS_KEY);
    const defaultSettings = {
      intervalDays: 1,
      autoRefresh: false, // Always false for revenue dashboard
      refreshInterval: 30,
    };
    
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        return {
          intervalDays: parsedSettings.intervalDays ?? defaultSettings.intervalDays,
          autoRefresh: false, // Force false for revenue dashboard
          refreshInterval: parsedSettings.refreshInterval ?? defaultSettings.refreshInterval,
        };
      } catch (error) {
        console.warn('Failed to parse revenue dashboard settings from localStorage:', error);
        return defaultSettings;
      }
    }
    
    return defaultSettings;
  });

  // Save settings to localStorage
  const saveSettings = useCallback((newSettings: DashboardSettings) => {
    const settingsToSave = { ...newSettings, autoRefresh: false }; // Force autoRefresh to false
    localStorage.setItem(REVENUE_DASHBOARD_SETTINGS_KEY, JSON.stringify(settingsToSave));
    setSettings(settingsToSave);
  }, []);

  // Fetch revenue data with reload protection
  const fetchRevenueData = useCallback(async () => {
    if (!canViewOrders || isReloading) {
      console.log('Skipping revenue data fetch:', !canViewOrders ? 'No permission' : 'Reload already in progress');
      return;
    }
    
    try {
      setIsReloading(true);
      setLoading(true);
      setError(null);
      const data = await apiService.getRevenueData(
        settings.intervalDays || 1
      );
      setRevenueData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError('Failed to fetch revenue data');
    } finally {
      setLoading(false);
      setIsReloading(false);
    }
  }, [settings.intervalDays, canViewOrders]); // Remove isReloading from dependencies

  // Initial data fetch and when filter settings change
  useEffect(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  // No need to listen for any shared settings

  // Event handlers
  const handleIntervalChange = (intervalDays: number) => {
    const newSettings = { ...settings, intervalDays };
    saveSettings(newSettings);
  };

  const getIntervalText = (days: number) => {
    if (days === -1) return 'Yesterday';
    if (days === 1) return 'Today';
    if (days === 2) return 'Last 2 days';
    if (days === 7) return 'Last 7 days';
    return `Last ${days} days`;
  };

  if (!canViewOrders) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view revenue data. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Settings Panel */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, cursor: 'pointer' }} onClick={() => setShowSettings(!showSettings)}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Revenue Dashboard Settings
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" gap={1}>
                <Chip 
                  label={getIntervalText(settings.intervalDays)} 
                  color="primary" 
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    const intervals = [-1, 1, 2, 7];
                    const currentIndex = intervals.indexOf(settings.intervalDays);
                    const nextIndex = (currentIndex + 1) % intervals.length;
                    handleIntervalChange(intervals[nextIndex]);
                  }}
                />
                {/* Day start hour chip removed */}
                <Chip 
                  label="Manual Refresh Only" 
                  color="info" 
                  size="small"
                  variant="outlined"
                />
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  fetchRevenueData(); 
                }}
                disabled={isReloading}
                sx={{
                  ...(isReloading && {
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  })
                }}
              >
                <Refresh />
              </IconButton>
              <IconButton size="small" onClick={() => setShowSettings(!showSettings)}>
                {showSettings ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Collapse in={showSettings}>
          <Box sx={{ p: 3, pt: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 250px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Interval</InputLabel>
                  <Select
                    value={settings.intervalDays}
                    label="Time Interval"
                    onChange={(e) => handleIntervalChange(Number(e.target.value))}
                  >
                    <MenuItem value={-1}>Yesterday</MenuItem>
                    <MenuItem value={1}>Today</MenuItem>
                    <MenuItem value={2}>Last 2 days</MenuItem>
                    <MenuItem value={7}>Last 7 days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {/* Day start hour select removed */}
              <Box sx={{ flex: '1 1 250px' }}>
                {lastUpdated && (
                  <Typography variant="caption" color="textSecondary">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Revenue Grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Revenue Summary Header */}
        <Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <AttachMoney sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="600" color="primary.main">
              Revenue Summary
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Restaurant revenue includes food items only, while Talabat total includes delivery fees (net of any platform discounts)
          </Typography>
        </Box>
        
        {/* Revenue Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <RevenueCard
            title="Restaurant Revenue"
            amount={revenueData.sales.total}
            subtitle="Food items only (no delivery or platform fees)"
            icon={<Store />}
            color="primary"
            loading={loading}
            tooltipText="Sum of all order items (TotalItems) - This is the restaurant's net revenue from food items only, excluding delivery fees and Talabat discounts"
            comparisonAmount={revenueData.talabatSales.total}
            comparisonLabel="Platform fees & delivery charges"
          />
          <RevenueCard
            title="Talabat Total Sales"
            amount={revenueData.talabatSales.total}
            subtitle="Includes delivery fees (net of any Talabat discounts)"
            icon={<AttachMoney />}
            color="success"
            loading={loading}
            tooltipText="Sum of all order totals (Total) - This is the final amount after delivery fees are added and Talabat discounts are applied. Discounts reduce the total amount."
            comparisonAmount={revenueData.sales.total}
            comparisonLabel="Additional platform charges"
          />
        </Box>

        {/* Revenue Breakdown Formula */}
        <Box>
          <Card sx={{ mt: 2, border: `2px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <CompareArrows sx={{ color: 'info.main' }} />
                Revenue Calculation Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Understanding the relationship between restaurant revenue and Talabat total sales (discounts are already factored into totals)
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                {/* Restaurant Revenue */}
                <Box sx={{ 
                  p: 3, 
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140
                }}>
                  <Typography variant="subtitle1" fontWeight="600" color="primary.main" gutterBottom>
                    Restaurant Revenue
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold" gutterBottom>
                    ${revenueData.sales.total.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Items (food only)
                  </Typography>
                </Box>

                {/* Platform Adjustments */}
                <Box sx={{ 
                  p: 3, 
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.warning.main, 0.05),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140
                }}>
                  <Typography variant="subtitle1" fontWeight="600" color="warning.main" gutterBottom>
                    Platform Adjustments
                  </Typography>
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Net Platform Fees:</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      (Delivery fees minus discounts)
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight="600">
                      ${(revenueData.talabatSales.total - revenueData.sales.total).toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Discounts Applied:</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      (Separate tracking only)
                    </Typography>
                    <Typography variant="h6" color="error.main" fontWeight="600">
                      ${revenueData.discounts.total.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {/* Talabat Total */}
                <Box sx={{ 
                  p: 3, 
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 140
                }}>
                  <Typography variant="subtitle1" fontWeight="600" color="success.main" gutterBottom>
                    Talabat Total Sales
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold" gutterBottom>
                    ${revenueData.talabatSales.total.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Restaurant Items + Net Platform Fees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Payment Summary Cards */}
        <Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 3, mb: 2 }}>
            <CreditCard sx={{ color: 'info.main' }} />
            <Typography variant="h6" fontWeight="600" color="info.main">
              Payment Method Summary
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CreditCard sx={{ color: 'info.main' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">Prepaid Orders</Typography>
                  <Typography variant="body2" color="text.secondary">Online payments</Typography>
                </Box>
              </Box>
              <Typography variant="h4" color="info.main" fontWeight="bold" gutterBottom>
                {revenueData.paymentSummary.prepaid.count}
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Restaurant Revenue</Typography>
                  <Typography variant="h6" color="primary.main" fontWeight="600">
                    ${revenueData.paymentSummary.prepaid.sales.toFixed(2)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">Talabat Total</Typography>
                  <Typography variant="h6" color="success.main" fontWeight="600">
                    ${revenueData.paymentSummary.prepaid.talabatSales.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Money sx={{ color: 'success.main' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">Cash Orders</Typography>
                  <Typography variant="body2" color="text.secondary">Cash on delivery</Typography>
                </Box>
              </Box>
              <Typography variant="h4" color="success.main" fontWeight="bold" gutterBottom>
                {revenueData.paymentSummary.cash.count}
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Restaurant Revenue</Typography>
                  <Typography variant="h6" color="primary.main" fontWeight="600">
                    ${revenueData.paymentSummary.cash.sales.toFixed(2)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">Talabat Total</Typography>
                  <Typography variant="h6" color="success.main" fontWeight="600">
                    ${revenueData.paymentSummary.cash.talabatSales.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Additional Information */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manager Revenue Dashboard
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          This dashboard provides comprehensive revenue analysis and financial reporting for management decisions. 
          Data is refreshed manually to ensure accuracy for financial reporting.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          • <strong>Revenue Summary:</strong> Detailed breakdown of restaurant revenue vs platform totals<br/>
          • <strong>Platform Adjustments:</strong> Analysis of delivery fees, discounts, and platform charges<br/>
          • <strong>Payment Methods:</strong> Revenue split by payment type for cash flow analysis<br/>
          • <strong>Manual Refresh Only:</strong> Prevents automated updates during financial review processes<br/>
          • <strong>Time Intervals:</strong> Flexible reporting periods for daily, weekly, or custom analysis
        </Typography>
      </Paper>
    </Box>
  );
};

export default RevenueDashboard;
